import { useRef, useState, useCallback } from "react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { IMG } from "@/lib/site";

export default function BeforeAfter() {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const ref = useRef(null);

  const update = useCallback((clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  }, []);

  return (
    <section id="avant-apres" className="py-24 lg:py-32">
      <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-5 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
        <div>
          <SectionHead
            tag="Avant / Après"
            title={<>Un écran brisé, <span className="text-neon">comme neuf</span></>}
            sub="Glissez le curseur : à gauche l'appareil que vous nous confiez, à droite ce que vous récupérez. Même matériau, seconde vie."
          />
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-3">
              <span className="rounded-full border border-line bg-ink px-4 py-2 text-xs font-semibold text-slate-400">
                Remontage soigné · étanchéité au châssis
              </span>
              <span className="rounded-full border border-line bg-ink px-4 py-2 text-xs font-semibold text-slate-400">
                Test tactile, luminosité & True Tone
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            ref={ref}
            data-testid="before-after-slider"
            role="slider"
            aria-label="Comparateur avant / après"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pos)}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
              if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
            }}
            onPointerDown={(e) => { setDragging(true); e.currentTarget.setPointerCapture(e.pointerId); update(e.clientX); }}
            onPointerMove={(e) => dragging && update(e.clientX)}
            onPointerUp={() => setDragging(false)}
            onPointerCancel={() => setDragging(false)}
            className="relative aspect-[16/10] w-full cursor-ew-resize select-none overflow-hidden rounded-2xl border border-line bg-ink"
            style={{ touchAction: "pan-y" }}
          >
            <img src={IMG.restored} alt="Smartphone réparé, écran impeccable" className="absolute inset-0 h-full w-full object-cover" draggable={false} loading="lazy" />
            <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
              <img src={IMG.broken} alt="Smartphone à l'écran brisé" className="h-full w-full object-cover" draggable={false} loading="lazy" />
            </div>

            <span className="absolute left-4 top-4 rounded-full border border-red-400/40 bg-night/80 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400 backdrop-blur-xl">
              Avant
            </span>
            <span className="absolute right-4 top-4 rounded-full border border-neon/40 bg-night/80 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neon backdrop-blur-xl">
              Après
            </span>

            <div className="absolute inset-y-0 w-0.5 bg-neon" style={{ left: `${pos}%` }}>
              <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-neon bg-night text-neon chip-glow">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3 4 7l4 4" /><path d="M4 7h16" /><path d="m16 21 4-4-4-4" /><path d="M20 17H4" />
                </svg>
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
